import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  FormControl,
  FormHelperText,
  Select
} from "@mui/material";

import Autocomplete from '@mui/material/Autocomplete';
import { useLocation, useNavigate } from 'react-router-dom';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { stateList } from "../../common/StaticFieldList";
import { useDispatch, useSelector } from "react-redux";
import { saveLocationAction, updateLocationAction,
  getCompanyGroupWithStateAction,
  getCompanyGroupWithoutStateAction
 } from "../../redux/actions/locationActions";
 import { fetchCompanyGroups } from '../../redux/actions/agencyActions';
 import { convertToE164 } from "../../common/helpers/commonFun";
import PhoneNumberField from "../../common/PhoneNumberField";
import { searchCitiesAction } from "../../redux/actions/cityAddressActions";
import { debounce } from "lodash";

const AddLocationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationData = location.state?.locationData || null;

  const user = useSelector((state) => state.user.user);
  const [customerId, setCustomerId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formValues, setFormValues] = useState({
    description: "",
    agencyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    secondaryPhone: "",
    faxNumber: "",
    companyGroupId: null,
    firstName: "",
    lastName: "",
    cellPhone: "",
    email: "",
    password: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [formTouched, setFormTouched] = useState(false);
  const [citySearchTerm, setCitySearchTerm] = useState("");
   const [cityOptions, setCityOptions] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const cityData = useSelector((state) => state.cityAddress?.cities || []);
  
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [carrierInputValue, setCarrierInputValue] = useState('');

  const carrier_group_list = useSelector((state) => state?.agency?.carrier_group_list || []);
  const carrier_group = useSelector((state) => state?.location?.get_carrier_group_name || []);

   useEffect(() => {
      const uniqueCities = cityData.reduce((acc, city) => {
        const key = `${city.cityName}-${city.stateCode}`;
        if (!acc.find((c) => `${c.cityName}-${c.stateCode}` === key)) {
          acc.push({
            ...city,
            displayName: `${city.cityName}, ${city.stateCode}`,
            searchKey: city.cityName.toLowerCase(),
          });
        }
        return acc;
      }, []);
      setCityOptions(
        uniqueCities.sort((a, b) => a.cityName.localeCompare(b.cityName))
      );
    }, [cityData]);

  useEffect(() => {
    dispatch(fetchCompanyGroups());
  }, [dispatch]);

  useEffect(() => {
    setCarrierOptions(carrier_group_list);
  }, [carrier_group_list]);

  useEffect(() => {
    if (user) {
      setCustomerId(user.id || null);
    }
  }, [user]);

  useEffect(() =>{
    dispatch(getCompanyGroupWithoutStateAction());
  }, [])

  useEffect(() => {
  if (locationData) {
    setFormValues({
      id: locationData.id,
      description: locationData.description || "",
      agencyName: locationData.agencyName || "",
      address1: locationData.address1 || "",
      address2: locationData.address2 || "",
      city: locationData.city || "",
      state: locationData.state || "",
      zipCode: locationData.zipCode || "",
      phone: normalizePhone(locationData.phone),
      secondaryPhone: normalizePhone(locationData.secondaryPhone),
      cellPhone: normalizePhone(locationData.cellPhone),
      faxNumber: normalizePhone(locationData.faxNumber),
      companyGroupId: locationData.companyGroupId || "",
      firstName: locationData.firstName || "",
      lastName: locationData.lastName || "",
      email: locationData.email || "",
      password: locationData.password || "",
    });

    setIsActive(locationData.isActive || false);

    if (locationData.state) {
      dispatch(getCompanyGroupWithStateAction(locationData.state));
    }
  }
}, [locationData, dispatch]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateLetterInput = (value) => /^[a-zA-Z\s]*$/.test(value);
  const validateAddressInput = (value) => /^[a-zA-Z0-9\s,#&()]*$/.test(value);
  const validatePassword = (value) => value.length >= 6 && /\d/.test(value);
  const validateEmail = (value) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,4}$/;
  const atCount = (value.match(/@/g) || []).length;
  return emailRegex.test(value) && atCount === 1;
};

  const validateZipCode = (value) => /^\d{5}$/.test(value);

  const validatePhoneNumber = (value) => {
    const cleanedValue = value.replace(/\D/g, "");
    return cleanedValue.length === 10;
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");

    const trimmed = cleaned.slice(0, 10);

    if (trimmed.length <= 3) {
      return trimmed;
    } else if (trimmed.length <= 6) {
      return `${trimmed.slice(0, 3)}-${trimmed.slice(3)}`;
    } else {
      return `${trimmed.slice(0, 3)}-${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
    }
  };

 const formatName = (value) => {
  return value.replace(/[^a-zA-Z\s]/g, "");
};

const formatEmail = (value) => {
  let cleaned = value.replace(/[^a-zA-Z0-9@._-]/g, '');

  const atParts = cleaned.split('@');
  if (atParts.length > 2) {
    cleaned = atParts[0] + '@' + atParts[1];
  }

  if (cleaned.includes('@')) {
    const [local, domain] = cleaned.split('@');

    let fixedDomain = domain
      .replace(/\.\.+/g, '.')
      .replace(/^\.+/, '');
    cleaned = local + '@' + fixedDomain;
  }

  return cleaned;
};

const normalizePhone = (value) => {
  if (!value) return '';
  const cleaned = value.replace('+1', '').replace(/\D/g, '');
  return formatPhoneNumber(cleaned);
};

  const validateField = (name, value) => {
    let error = "";

   const requiredFields = [
  "description",
  "agencyName",
  "address1",
  "city",
  "state",
  "zipCode",
  "phone",
  "faxNumber",
  "firstName",
  "lastName",
  "cellPhone",
  "email",
  "password",
  "requiredFields"
];


    if (requiredFields.includes(name) && (!value || value.trim() === "")) {
      return "This field is required.";
    }

    const characterLimits = {
      address1: 75,
      address2: 75,
      description: 50,
      agencyName: 50,
      city: 50,
      firstName: 50,
      lastName: 50,
      zipCode: 5,
      phone: 12,
      secondaryPhone: 12,
      cellPhone: 12,
      faxNumber: 12,
      email: 50,
      password: 50,
    };

    if (name === "zipCode") {
      if (value && !validateZipCode(value)) {
        error = "Zip Code must be exactly 5 numeric digits.";
      }
    }
    else if (["phone", "secondaryPhone", "cellPhone", "faxNumber"].includes(name)) {
      if (value && !validatePhoneNumber(value)) {
        error = `${name.charAt(0).toUpperCase() + name.slice(1)} must be exactly 10 digits.`;
      }
    }
    else if (characterLimits[name] && value.length > characterLimits[name]) {
      error = `${name.charAt(0).toUpperCase() + name.slice(1)} must be ${characterLimits[name]} characters or less.`;
    } else if (
      ["city", "firstName", "lastName"].includes(name) &&
      value && !validateLetterInput(value)
    ) {
      error = `${name.charAt(0).toUpperCase() + name.slice(1)}  Only letters and spaces are allowed.`;
    }
     else if (["address1", "address2"].includes(name) && value && !validateAddressInput(value)) {
      error = `${name === "address1" ? "Primary Address" : "Secondary Address"} contains invalid characters.`;
    } else if (name === "email") {
      if (value && !validateEmail(value)) {
        error = "Invalid email format.";
      }
    } else if (name === "password") {
      if (value && !validatePassword(value)) {
        error = "Password must be at least 6 characters and include a number.";
      }
    }

    return error;
  };

   const debouncedCitySearch = useCallback(
      debounce((searchTerm, stateCode = "") => {
        if (searchTerm.length >= 2) {
          dispatch(searchCitiesAction(searchTerm, stateCode, 20));
        }
      }, 300),
      [dispatch]
    );

  const handleInputChange = (event) => {
  const { name, value } = event.target;
  let formattedValue = value;

  if (!formTouched) {
    setFormTouched(true);
  }

  if (["firstName", "lastName"].includes(name)) {
    formattedValue = formatName(value);
  }

  if (name === "email") {
    formattedValue = formatEmail(value);
  }

  if (["phone", "secondaryPhone", "cellPhone", "faxNumber"].includes(name)) {
    formattedValue = formatPhoneNumber(value);
  } else if (name === "zipCode") {
    formattedValue = value.replace(/\D/g, "").slice(0, 5);
  }

  setFormValues({
    ...formValues,
    [name]: formattedValue,
  });

  if (name === "state") {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const errorMessage = validateField(name, formattedValue);
  if (errorMessage) {
    setFormErrors({ ...formErrors, [name]: errorMessage });
  } else if (formErrors[name]) {
    const updatedErrors = { ...formErrors };
    delete updatedErrors[name];
    setFormErrors(updatedErrors);
  }
};

const validateAllFields = () => {
    const newErrors = {};

    Object.keys(formValues).forEach(field => {
      const errorMessage = validateField(field, formValues[field]);
      if (errorMessage) {
        newErrors[field] = errorMessage;
      }
    });

    return newErrors;
  };

  const handleSave = () => {
    
    const newErrors = validateAllFields();

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }
    const dataToSubmit = {
      ...formValues,
      cellPhone:convertToE164(formValues.cellPhone),
      phone:convertToE164(formValues.phone),
      secondaryPhone:convertToE164(formValues.secondaryPhone),
      faxNumber:convertToE164(formValues.faxNumber),
      customerId,
      isActive
    };

    const onSuccess = () => {
      setSnackbarMessage("Location updated successfully.");
      setOpenSnackbar(true);
      setTimeout(() => {
        navigate('/LocationList');
      }, 2000);
    };

    const onSuccesssave = () => {
      setSnackbarMessage("Location saved successfully.");
      setOpenSnackbar(true);
      setTimeout(() => {
        navigate('/LocationList');
      }, 2000);
    };

    if (locationData && locationData.id) {
      dispatch(updateLocationAction(dataToSubmit, onSuccess));
    } else {
      dispatch(saveLocationAction(dataToSubmit, onSuccesssave));
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  useEffect(() => {
  if (formValues.companyGroupId) {
    const selectedFromSearchList = carrier_group_list.find(
      (item) => item.id === formValues.companyGroupId
    );

    const selectedFromStaticList = carrier_group.find(
      (item) => item.id === formValues.companyGroupId
    );

    const selected = selectedFromSearchList || selectedFromStaticList;

    if (selected) {
      setCarrierInputValue(selected.name);

      setCarrierOptions((prev) => {
        const exists = prev.some((item) => item.id === selected.id);
        return exists ? prev : [...prev, selected];
      });
    }
  }
}, [formValues.companyGroupId, carrier_group_list, carrier_group]);

const handleCancelFrom = () => {
  navigate('/LocationList');
}

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "#fff", fontFamily: '"Poppins", sans-serif' }}>
      <Box sx={{ width: "100%", maxWidth: "900px", padding: "0px", backgroundColor: "#fff", border: "1px solid #e3e3e3" }}>
        <Box sx={{ padding: "32px" }}>
          <Typography variant="h2" sx={{ marginBottom: "3px" }}>
            {location?.state?.locationData ? "Edit Location" : "Add Location"}
          </Typography>
        </Box>
        <hr
          style={{
            borderTop: "1px solid #ccc",
            width: "100%",
            margin: "0",
          }}
        />
        <Box sx={{ padding: "32px" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: "24px",
              marginBottom: "24px",
            }}
          >
            <Box>
              <Typography variant="body2" component="label" htmlFor="description" sx={{ display: "block", marginBottom: "4px" }}>
                Location Description <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="description"
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                error={!!formErrors.description}
                helperText={formErrors.description || ""}
                fullWidth
                type="text"
                inputProps={{ maxLength: 50 }}
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="agencyName" sx={{ display: "block", marginBottom: "4px" }}>
                Agency Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="agencyName"
                name="agencyName"
                value={formValues.agencyName}
                onChange={handleInputChange}
                error={!!formErrors.agencyName}
                helperText={formErrors.agencyName || ""}
                fullWidth
                type="text"
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="address1" sx={{ display: "block", marginBottom: "4px" }}>
                Address Line 1 <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="address1"
                name="address1"
                value={formValues.address1}
                onChange={handleInputChange}
                error={!!formErrors.address1}
                helperText={formErrors.address1 || ""}
                fullWidth
                type="text"
                inputProps={{ maxLength: 50, pattern: "^[a-zA-Z0-9\\s]*$" }}
              />
            </Box>
            <Box>
              <Typography variant="body2" component="label" htmlFor="address2" sx={{ display: "block", marginBottom: "4px" }}>
                Address Line 2
              </Typography>
              <TextField
                id="address2"
                name="address2"
                value={formValues.address2}
                onChange={handleInputChange}
                error={!!formErrors.address2}
                helperText={formErrors.address2 || ""}
                fullWidth
                type="text"
              />
            </Box>

           <Box>
  <Typography
    variant="body2"
    component="label"
    htmlFor="city-input"
    sx={{ display: "block", marginBottom: "4px" }}
  >
    City <span style={{ color: "red" }}>*</span>
  </Typography>

  <Autocomplete
    id="city-input"
    options={cityOptions || []}
    getOptionLabel={(option) =>
      option.displayName || option.cityName || ""
    }
    inputValue={formValues.city}
    value={selectedCity || null}
    onInputChange={(event, newValue, reason) => {
      if (reason === "input") {
        setCitySearchTerm(newValue);
        setSelectedCity(null);
        // optional: call debounced city API search
        debouncedCitySearch?.(newValue, formValues.state);

        setFormValues((prev) => ({
          ...prev,
          city: newValue,
        }));

        setFormErrors((prev) => ({
          ...prev,
          city: validateField("city", newValue),
        }));
      } else if (reason === "clear") {
        setCitySearchTerm("");
        setSelectedCity(null);
        setFormValues((prev) => ({
          ...prev,
          city: "",
        }));
        setFormErrors((prev) => ({
          ...prev,
          city: validateField("city", ""),
        }));
      }
    }}
    onChange={(event, selectedOption) => {
      if (selectedOption) {
        setSelectedCity(selectedOption);
        setCitySearchTerm(selectedOption.displayName);
        setFormValues((prev) => ({
          ...prev,
          city: selectedOption.cityName,
          state: selectedOption.stateCode || prev.state,
        }));
        setFormErrors((prev) => ({
          ...prev,
          city: "",
        }));
      }
    }}
    filterOptions={(x) => x} // disable MUI filtering to use backend results
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder="Start typing city name..."
        error={!!formErrors.city}
        helperText={formErrors.city || ""}
        fullWidth
        onBlur={() => {
          const err = validateField("city", formValues.city);
          if (err) {
            setFormErrors((prev) => ({ ...prev, city: err }));
          }
        }}
        sx={{
          "& fieldset": {
            borderColor: "#C1C1C1",
          },
          "& .MuiInputBase-root": {
            height: "52px",
            backgroundColor: "#fff",
            fontSize: "16px",
            fontFamily: '"Poppins", sans-serif',
          },
          "& .MuiInputBase-input": {
            color: "#000",
          },
        }}
      />
    )}
  />
</Box>


            <Box>
              <Typography variant="body2" component="label" htmlFor="state-select" sx={{ display: "block", marginBottom: "4px" }}>
                State <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth error={!!formErrors.state}>
                <Select
                  native
                  value={formValues.state}
                  onChange={handleInputChange}
                  inputProps={{
                    name: "state",
                    id: "state-select",
                  }}
                  sx={{
                    width: "100%",
                    fontFamily: 'Poppins',
                    backgroundColor: "#ffffff",
                    borderRadius: "4px",
                    '& .MuiSelect-select': {
                      border: "1px solid #ccc",
                      padding: "10px",
                    },
                    '&:focus': {
                      '& .MuiSelect-select': {
                        borderColor: 'black',
                      }
                    },
                    '&:hover': {
                      '& .MuiSelect-select': {
                        borderColor: '#999',
                      }
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "auto",
                        maxWidth: "none",
                      },
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <option value="" disabled>Select a state</option>
                  {stateList.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </Select>
                {formErrors.state && <FormHelperText>{formErrors.state}</FormHelperText>}
              </FormControl>
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="zipCode" sx={{ display: "block", marginBottom: "4px" }}>
                Zip Code <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="zipCode"
                name="zipCode"
                value={formValues.zipCode}
                onChange={handleInputChange}
                error={!!formErrors.zipCode}
                helperText={formErrors.zipCode || ""}
                fullWidth
                type="text"
              />
            </Box>

            <Box>
             
              <PhoneNumberField
                id="phone"
                name="phone"
                value={formValues.phone}
                onChange={handleInputChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone || ""}
                fullWidth
                type="text"
                label="Phone"
                labelStyle={{ fontSize: 14, fontWeight: 400,mb:0.5}}
              />
            </Box>

            <Box>
              <PhoneNumberField
                id="secondaryPhone"
                name="secondaryPhone"
                value={formValues.secondaryPhone}
                onChange={handleInputChange}
                error={!!formErrors.secondaryPhone}
                helperText={formErrors.secondaryPhone || ""}
                fullWidth
                type="text"
                label="Alternate Phone"
                labelStyle={{ fontSize: 14, fontWeight: 400,mb:0.5}}
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="faxNumber" sx={{ display: "block", marginBottom: "4px" }}>
                Fax Number <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="faxNumber"
                name="faxNumber"
                value={formValues.faxNumber}
                onChange={handleInputChange}
                error={!!formErrors.faxNumber}
                helperText={formErrors.faxNumber || ""}
                fullWidth
                type="text"
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="firstName" sx={{ display: "block", marginBottom: "4px" }}>
                First Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="firstName"
                name="firstName"
                value={formValues.firstName}
                onChange={handleInputChange}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName || ""}
                fullWidth
                type="text"
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="lastName" sx={{ display: "block", marginBottom: "4px" }}>
                Last Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="lastName"
                name="lastName"
                value={formValues.lastName}
                onChange={handleInputChange}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName || ""}
                fullWidth
                type="text"
              />
            </Box>

            <Box>
             
              <PhoneNumberField
                id="cellPhone"
                name="cellPhone"
                value={formValues.cellPhone}
                onChange={handleInputChange}
                error={!!formErrors.cellPhone}
                helperText={formErrors.cellPhone || ""}
                fullWidth
                type="text"
                label=" Cell Phone"
                labelStyle={{ fontSize: 14, fontWeight: 400,mb:0.5}}
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="email" sx={{ display: "block", marginBottom: "4px" }}>
                Email <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="email"
                name="email"
                value={formValues.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email || ""}
                fullWidth
                type="text"
              />
            </Box>

            <Box>
              <Typography variant="body2" component="label" htmlFor="password" sx={{ display: "block", marginBottom: "4px" }}>
                Password <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                id="password"
                name="password"
                value={formValues.password}
                onChange={handleInputChange}
                error={!!formErrors.password}
                helperText={formErrors.password || ""}
                fullWidth
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

         <Box>
  <Typography
    id="carrier-group"
    variant="body2"
    component="label"
    htmlFor="carrier-group"
    sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: "4px" }}
  >
    Carrier Group <span style={{ color: "red", marginLeft: "4px" }}>*</span>
  </Typography>

  <Autocomplete
    id="carrier-group-autocomplete"
    options={carrierOptions}
    getOptionLabel={(option) => option?.name || ""}
    inputValue={carrierInputValue}
    value={carrierOptions.find((option) => option.id === formValues.companyGroupId) || null}
    onInputChange={(event, newInputValue) => setCarrierInputValue(newInputValue)}
    onChange={(event, newValue) => {
      setCarrierInputValue(newValue?.name || "");
      setFormValues((prev) => ({
        ...prev,
        companyGroupId: newValue?.id || null,
      }));
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        id="carrier-group-input"
        aria-labelledby="carrier-group-label"
        placeholder="Type carrier group name"
        fullWidth
        error={!!formErrors.companyGroupId}
        helperText={formErrors.companyGroupId || ""}
      />
    )}
  />
</Box>
          </Box>

          <FormControlLabel
            control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label="Is Active"
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "32px" }}>
            <Button variant="outlined" size="large"
            sx={{ marginRight: "16px", '&:focus': {
                  outline: '1px solid #E69C00',
                }, }} onClick={handleCancelFrom}>
              Cancel
            </Button>

            <Button
              size="large"
              variant="contained"
              onClick={handleSave}
            >
              {location?.state?.locationData ? "Update" : "Save"}
            </Button>
          </Box>

          <Snackbar
            open={openSnackbar}
            autoHideDuration={2000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}

export default AddLocationForm;